/**
 * The tiptap extension set behind MarkdownEditor, plus the markdown
 * serialization fixes it needs to round-trip Auger documents losslessly.
 *
 * Kept out of the component so unit tests exercise the exact same schema the
 * editor runs — a markdown construct the schema doesn't know is silently
 * dropped on parse and the loss is saved back on the next edit.
 *
 * Fixes layered over the stock extensions:
 *  - Paragraph: escape text that would re-parse as block syntax at a line
 *    start (`# `, `1. `, `- `, `+ `, setext `---`/`===`). @tiptap/markdown only
 *    escapes inline delimiters, so typing "1. not a list" saved a list.
 *  - Table: cells hold paragraphs only (all GFM can store); escape `|` inside cells (a literal pipe or a `[[id|label]]`
 *    reference otherwise splits the cell), keep multi-paragraph cells as
 *    `<br>`-joined lines, and drop the blank-line padding around the table.
 *  - Underline: serialize as `<u>…</u>` (read by marked + DOMPurify) instead
 *    of tiptap's `++…++`, which the read view shows literally.
 *  - Image + task lists: added so `![alt](src)` and `- [ ]` survive editing
 *    (a paragraph holding only an image stays a paragraph — images are inline).
 */
import Image from "@tiptap/extension-image"
import Link from "@tiptap/extension-link"
import { TaskItem, TaskList } from "@tiptap/extension-list"
import Paragraph from "@tiptap/extension-paragraph"
import {
  DEFAULT_CELL_LINE_SEPARATOR,
  renderTableToMarkdown,
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from "@tiptap/extension-table"
import Underline from "@tiptap/extension-underline"
import { Markdown } from "@tiptap/markdown"
import StarterKit from "@tiptap/starter-kit"
import type { AnyExtension, Editor, MarkdownRendererHelpers } from "@tiptap/vue-3"
import { Marked, marked } from "marked"
import { RecordReference } from "./RecordReferenceNode"

/**
 * Backslash-escape the first character of any line that would otherwise start
 * a block construct when the paragraph is parsed again. Applied per line
 * because ATX headings, lists and setext underlines can all interrupt a
 * paragraph after a soft or hard line break.
 */
export function escapeBlockStarts(markdown: string): string {
  return markdown
    .split("\n")
    .map((line) =>
      line
        // ATX heading: "# x", "###"
        .replace(/^(\s*)(#{1,6})(?=\s|$)/, "$1\\$2")
        // Bullet list: "- x", "+ x", "* x" (a text `*` is already escaped)
        .replace(/^(\s*)([-+*])(?=\s)/, "$1\\$2")
        // Ordered list: "1. x", "2) x"
        .replace(/^(\s*)(\d{1,9})([.)])(?=\s|$)/, "$1$2\\$3")
        // Setext underline / thematic break made only of - or =
        .replace(/^(\s*)([-=])(\2*\s*)$/, "$1\\$2$3")
    )
    .join("\n")
}

/**
 * Escape every `|` not already escaped, so a cell's content can't split into
 * extra cells. GFM unescapes `\|` inside cells before inline parsing, which
 * also keeps `[[id|label]]` references and code spans intact.
 */
export function escapeTableCellPipes(text: string): string {
  let out = ""
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (ch === "\\" && i + 1 < text.length) {
      out += ch + text[i + 1]
      i++
    } else if (ch === "|") {
      out += "\\|"
    } else {
      out += ch
    }
  }
  return out
}

const MarkdownParagraph = Paragraph.extend({
  // The stock parser unwraps a paragraph holding only an image to block
  // level, which assumes block images. Images here are inline (so they work
  // mid-sentence), so keep the paragraph — a bare inline node at doc level is
  // schema-invalid and throws on the first edit.
  parseMarkdown(token, helpers) {
    const tokens = token.tokens || []
    if (tokens.length === 1 && tokens[0]?.type === "image") {
      return helpers.createNode("paragraph", undefined, helpers.parseInline(tokens))
    }
    return this.parent?.(token, helpers) ?? []
  },
  renderMarkdown(node, h, ctx) {
    const out = this.parent?.(node, h, ctx) ?? ""
    return escapeBlockStarts(out)
  },
})

const MarkdownTable = Table.extend({
  renderMarkdown(node, h) {
    const cellHelpers: MarkdownRendererHelpers = {
      ...h,
      renderChildren: (nodes, separator) =>
        escapeTableCellPipes(h.renderChildren(nodes, separator)),
    }
    return renderTableToMarkdown(node, cellHelpers, {
      cellLineSeparator: DEFAULT_CELL_LINE_SEPARATOR,
    }).trim()
  },
})

// GFM cells hold one line of inline content, so a heading or list pasted or
// typed into a cell can't be written back (it flattened to "- one- two").
// Paragraph-only cells make the editor refuse what markdown can't store;
// multiple paragraphs are kept as `<br>`-joined lines.
const MarkdownTableHeader = TableHeader.extend({ content: "paragraph+" })
const MarkdownTableCell = TableCell.extend({ content: "paragraph+" })

const MarkdownUnderline = Underline.extend({
  renderMarkdown(node, h) {
    return `<u>${h.renderChildren(node)}</u>`
  },
})

/**
 * Normalize stored markdown before it's handed to the editor. A `[[id|label]]`
 * reference written with a bare `|` inside a table row (raw-mode edits, MCP
 * tools) would otherwise split its cell, so escape that separator on
 * table-row lines — the form the editor itself writes.
 */
export function prepareMarkdown(markdown: string): string {
  if (!markdown.includes("[[")) return markdown
  return markdown
    .split("\n")
    .map((line) => (/^\s*\|/.test(line) ? line.replace(/\[\[(\d+)\|/g, "[[$1\\|") : line))
    .join("\n")
}

/** Build the editor's extension list. `extra` are wrapper-supplied extensions
 *  (e.g. DocumentEditor's `@`-mention). */
export function markdownExtensions(extra: AnyExtension[] = []): AnyExtension[] {
  return [
    StarterKit.configure({ link: false, paragraph: false, underline: false }),
    MarkdownParagraph,
    MarkdownUnderline,
    // Resizing is off — column widths have no markdown representation.
    MarkdownTable.configure({ resizable: false }),
    TableRow,
    MarkdownTableHeader,
    MarkdownTableCell,
    TaskList,
    TaskItem.configure({ nested: true }),
    Image.configure({ inline: true }),
    // A private marked instance per editor. @tiptap/markdown registers its
    // tokenizers with `marked.use()`, which on the default (global) instance
    // leaks editor-only tokens (taskList, underline, …) into every other
    // marked.parse() in the app — the read view throws on them — and piles up
    // a duplicate set on each editor mount.
    // (A Marked instance has the same parse API; the option is typed as the
    // default export.)
    Markdown.configure({ marked: new Marked() as unknown as typeof marked }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: { class: "editor-link" },
    }),
    RecordReference,
    ...extra,
  ]
}

/**
 * The editor's content as markdown, without the blank-line padding tiptap
 * leaves at the edges (e.g. from the trailing empty paragraph) — so an
 * unchanged document serializes identically and doesn't read as an edit.
 */
export function serializeMarkdown(editor: Editor): string {
  return editor
    .getMarkdown()
    .replace(/^(?:[ \t]*\n)+/, "")
    .replace(/\s+$/, "")
}

// Render markdown to HTML with the incidental differences removed — paragraph
// wrappers (tight vs loose lists), whitespace between/inside tags — so two
// sources compare equal when they display the same content.
function canonicalHtml(markdown: string): string {
  return (marked.parse(markdown, { async: false }) as string)
    .replace(/<\/?p>/g, " ")
    .replace(/<br\s*\/?>/g, "<br>")
    .replace(/\s+/g, " ")
    .replace(/\s*(<[^>]+>)\s*/g, "$1")
    .trim()
}

/**
 * Whether `serialized` (what the editor wrote back) displays the same as
 * `source`. Formatting-only rewrites (`_x_` → `*x*`, `+` → `-`, table column
 * padding) pass; content the schema dropped (raw HTML, comments) fails.
 */
export function isLosslessRoundTrip(source: string, serialized: string): boolean {
  if (source.trim() === serialized.trim()) return true
  return canonicalHtml(prepareMarkdown(source)) === canonicalHtml(serialized)
}

// Block syntax at a line start, or unambiguous inline syntax (bold, links,
// inline code). A lone `*` or `_` in prose isn't enough.
const MARKDOWN_HINT_RE =
  /^(\s{0,3}(#{1,6}\s|[-*+]\s+\[[ xX]\]\s|[-*+]\s|\d{1,9}[.)]\s|>\s?|```|~~~|\|.*\|\s*$))|\*\*[^*\n]+\*\*|\[[^\]\n]+\]\([^)\s]+\)|`[^`\n]+`/m

/** Heuristic: is this pasted plain text markdown worth parsing? */
export function looksLikeMarkdown(text: string): boolean {
  return MARKDOWN_HINT_RE.test(text)
}

/**
 * Normalize a URL typed into the link editor: trims, and adds `https://` to a
 * bare domain ("example.com/x") so it doesn't become a broken relative link.
 * Leaves schemes, anchors, and relative/absolute paths alone.
 */
export function normalizeLinkUrl(input: string): string {
  const url = input.trim()
  if (!url) return ""
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) || /^[#/?.]/.test(url)) return url
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(url)) return `mailto:${url}`
  return `https://${url}`
}
